import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ActivityIcon } from '../../../../assets/icons/activety-icon.svg';
// import { ReactComponent as CarIcon } from '../../../../assets/icons/car.svg';
// import { ReactComponent as CardIcon } from '../../../../assets/icons/credit-card.svg';
// import { ReactComponent as RfidIcon } from '../../../../assets/icons/dot-circle.svg';
// import { ReactComponent as PlusIcon } from '../../../../assets/icons/element-plus-icon.svg';
// import { ReactComponent as ChargerIcon } from '../../../../assets/icons/gas-icon.svg';
import { ReactComponent as CardPosIcon } from '../../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as MoreIcon } from '../../../../assets/icons/more-icon.svg';
import { ReactComponent as ResetPasswordIcon } from '../../../../assets/icons/reset-password-v1-icon.svg';
// import { ReactComponent as FavoriteIcon } from '../../../../assets/icons/save-bookmark-icon.svg';
import profile from '../../../../assets/images/profiles/profile.jpg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { fetchBasicInformations } from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../../shared-components/custom-input/CustomPhoneInput';
import DropDownMenu from '../../../../shared-components/drop-down/DropDownMenu';
import ConfirmBox from '../../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import MapSidePanel from '../../../../shared-components/table-side-panel/MapSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import userApiV2 from '../../../../utils/api/end-consumer-v2';
import userApiV3 from '../../../../utils/api/v2/end-consumer';
// import DebitForm from '../FormComponents/DebitForm';
// import RefundForm from '../FormComponents/RefundForm';
import MyVehicle from '../SidePaneComponents/MyVehicle/MyVehicle';
import PaymentMethod from '../SidePaneComponents/PaymentMethod/PaymentMethod';
import WalletHistory from '../SidePaneComponents/WalletHistory/WalletHistory';

import ActivityTimeline from './ActivityTimeline';
import AddWallet from './AddWallet';
import PersonalChargers from './PersonalCharger';

interface MenuList {
  id: string;
  content: JSX.Element;
  allow: boolean;
  function: () => void;
  disabled: boolean;
}

/**
 * Component to display end consumer profile
 * @returns {any} - The ProfileConsumer component
 */
export default function ProfileConsumer() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [consumerStatus, setConsumerStatus] = useState<string>('');
  const [favoriteStationCount, setFavoriteStationCount] = useState<number>(0);
  const { loading, selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );

  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
  });
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => {},
  });

  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelData, setModelData] = useState<{
    title: string;
    content: React.ReactElement;
    width?: string | number;
  }>({
    title: '',
    content: <div />,
  });

  /**
   * Fetch Users list function
   * @param {any} userCustomId - The userCustomId
   */
  const fetchData = (userCustomId: string) => {
    if (!loading) {
      dispatch(fetchBasicInformations({ userCustomId }));
    }
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Sends a password reset link to the user's email for a specific consumer.
   * @param {string} currentData - The ID of the consumer whose password is to be reset.
   */
  const confirmResetPassword = (currentData: string) => {
    userApiV3
      .resetPassword(currentData, { submissionStatus: 'userCustomId' })
      .then(() => {
        setConfirmBox((previous) => ({ ...previous, status: false }));
        setModalView({
          status: true,
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('passwordResetSuccessNotification')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                closeModal();
              }}
            />
          ),
          rightCorner: '',
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message ||
              `Password reset failed for Consumer ID: ${currentData}.`,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Displays a confirmation prompt to reset the password for a specific consumer.
   */
  const resetPasswordFunction = () => {
    if (selectedEndConsumer?.userCustomId) {
      setConfirmBox({
        status: true,
        title: t('resetPasswordTitle'),
        subTitle: t('resetPasswordMessage'),
        /**
         * Triggers the password reset process for the specified consumer.
         * @returns {void} The result
         */
        conformSubmit: () => confirmResetPassword(selectedEndConsumer?.userCustomId), // ✅ Now correctly calls the function
        submitLabel: t('proceed'),
      });
    }
  };
  /**
   * Open Activity Log side panel
   */
  const handleLogActivity = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('activityLog'),
      content: <ActivityTimeline />,
    });
  };

  /**
   * Favorite Station
   */
  const handleFavoriteStation = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('favoriteStations'),
      content: <MapSidePanel />,
    });
  };

  /**
   * Personal Charger
   */
  const handlePersonalCharger = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('personalCharger'),
      content: <PersonalChargers />,
    });
  };

  /**
   * Open Vehicle Details side panel
   */
  const handleOpenVehicleDetails = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('vehicle'),
      content: <MyVehicle />,
    });
  };

  /**
   * Open Payment Method side panel
   */
  const handleOpenPaymentMethod = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('paymentMethod'),
      content: <PaymentMethod />,
    });
  };

  // /**
  //  * handle Debit
  //  */
  // const handleDebit = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Debit Form',
  //     content: <DebitForm />,
  //   });
  // };

  // /**
  //  * handle Refund
  //  */
  // const handleRefund = () => {
  //   setIsModelOpen(true);
  //   setModelData({
  //     title: 'Refund Form',
  //     content: <RefundForm />,
  //   });
  // };

  /**
   * Open Wallet History side panel
   */
  const renderWalletHistory = () => {
    setIsModelOpen(true);
    setModelData({
      title: t('walletHistory'),
      content: <WalletHistory />,
      width: '33vw',
    });
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    accountStatus: '',
    isActive: false,
    wallet: { balance: '0', deposit: '0' },
  });

  const [originalForm, setOriginalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '',
    wallet: { balance: '0', deposit: '0' },
  });

  /**
   * Populate edit form when entering edit mode
   */
  const handleProfileEditInformation = async () => {
    if (!selectedEndConsumer?.userCustomId) return;

    try {
      const response = await userApiV3.fetchBasicProfile(selectedEndConsumer.userCustomId);
      setOriginalForm(response.data);

      console.log('response.data', response.data);
      console.log('basicInformation', basicInformation);

      setEditForm(response.data);
      setConsumerStatus(response.data.accountStatus);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('updateFailed'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetch favorite stations count
   */
  const handleFetchFavoriteStations = async () => {
    if (!selectedEndConsumer?.userCustomId) return;
    try {
      const response = await userApiV2.fetchFavoriteStations(selectedEndConsumer.userCustomId);
      setFavoriteStationCount(response?.data?.results?.length || 0);
    } catch (error) {
      console.error('Failed to fetch favorite stations', error);
    }
  };

  /**
   * Handle input change
   */
  const handleChange = useCallback((key: keyof any, value: string) => {
    setEditForm((previous) => ({ ...previous, [key]: value }));
  }, []);
  /**
   * Save edited profile or update account status
   */
  const handleSave = useCallback(
    async (actionType: 'edit' | 'VIP' | 'ONHOLD' | 'BLOCKED' | 'RESUMED') => {
      if (!selectedEndConsumer?.userCustomId) return;

      let payload: Record<string, string> = {};

      switch (actionType) {
        case 'edit': {
          const hasOriginalPhone = Boolean(originalForm?.phoneNumber?.trim());
          const hasOriginalEmail = Boolean(originalForm?.email?.trim());

          if (
            (hasOriginalPhone && !editForm?.phoneNumber?.trim()) ||
            (hasOriginalEmail && !editForm?.email?.trim())
          ) {
            return;
          }
          const changedPayload: Record<string, string> = {};

          if (editForm.firstName !== originalForm.firstName) {
            changedPayload.firstName = editForm.firstName;
          }

          if (editForm.lastName !== originalForm.lastName) {
            changedPayload.lastName = editForm.lastName;
          }

          if (editForm.email !== originalForm.email) {
            changedPayload.email = editForm.email;
          }

          if (editForm.phoneNumber !== originalForm.phoneNumber) {
            changedPayload.phoneNumber = editForm.phoneNumber;
          }

          if (editForm.countryCode !== originalForm.countryCode) {
            changedPayload.countryCode = editForm.countryCode;
          }

          payload = changedPayload;
          break;
        }

        case 'VIP':
          payload = {
            accountStatus: 'VIP',
          };
          break;

        case 'ONHOLD':
          payload = {
            accountStatus: 'ONHOLD',
          };
          break;

        case 'BLOCKED':
          payload = {
            accountStatus: 'BLOCKED',
          };
          break;

        case 'RESUMED':
          payload = {
            accountStatus: 'RESUMED',
          };
          break;

        default:
          return;
      }

      try {
        if (actionType === 'edit' && Object.keys(payload).length === 0) {
          setIsEditMode(false);
          return;
        }

        setIsSaveLoading(true);

        await userApiV2.editBasicInformation(selectedEndConsumer.userCustomId, payload);
        setConsumerStatus(actionType);
        handleProfileEditInformation();
        
        if (actionType === 'edit') {
          setModalView({
            status: true,
            title: '',
            header: false,
            content: (
              <SuccessNotification
                title={t('consumerUpdatedSuccessfully')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  closeModal();
                }}
              />
            ),
            rightCorner: '',
          });
        } else {
          dispatch(
            setNotificationMessage({
              message: t('consumerUpdatedSuccessfully'),
              status: true,
              type: 'success',
              code: 200,
            }),
          );
        }

        setIsEditMode(false);

        // Refresh latest profile data
        dispatch(fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }));
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('updateFailed'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setIsSaveLoading(false);
      }
    },
    [dispatch, editForm, originalForm, selectedEndConsumer],
  );

  /**
   * Populate edit form when entering edit mode
   */
  const startEditMode = useCallback(() => {
    setIsEditMode(true);
  }, [basicInformation]);
  const isHold = consumerStatus === 'ONHOLD';
  // const isPaused = consumerStatus === 'BLOCKED';

  const menuList: MenuList[] = [
    // {
    //   id: 'toggle-status',
    //   content: <span>{isPaused ? t('resume') : t('pause')}</span>,
    //   allow: true,
    //   disabled: isHold, // ONHOLD disables everything
    //   /**
    //    * Resume || pause
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave(isPaused ? 'RESUMED' : 'BLOCKED'),
    // },
    // {
    //   id: 'hold',
    //   content: <span>{t('hold')}</span>,
    //   allow: true,
    //   disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
    //   /**
    //    * ONHOLD
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave('ONHOLD'),
    // },
    // {
    //   id: 'vip',
    //   content: <span>{t('markAsVip')}</span>,
    //   allow: true,
    //   disabled: isHold || isPaused, // Disabled when ONHOLD or BLOCKED
    //   /**
    //    * ONHOLD
    //    * @returns {void} - Triggers handleSave
    //    */
    //   function: () => handleSave('VIP'),
    // },
    {
      id: 'edit',
      content: <span>{t('edit')}</span>,
      allow: true,
      disabled: isHold, // Disabled only when ONHOLD
      /**
       * ONHOLD
       * @returns {void} - Triggers startEditMode
       */
      function: () => startEditMode(),
    },
  ];

  /**
   * Handle open modal
   */
  const handleOpenModal = () => {
    const endConsumerId = selectedEndConsumer?.userCustomId;
    if (!endConsumerId) return;
    setModalView({
      status: true,
      title: t('addMoney'),
      content: <AddWallet endConsumerId={endConsumerId} closeModal={closeModal} />,
      header: true,
      rightCorner: '',
    });
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchData(selectedEndConsumer.userCustomId);
      handleProfileEditInformation();
      handleFetchFavoriteStations();
    }
  }, [selectedEndConsumer]);

  const consumerIdDisplay =
    (originalForm as any)?.userCustomId ||
    selectedEndConsumer?.userCustomId ||
    basicInformation?.user?.userCustomId ||
    '-';

  const vehiclesList =
    (originalForm as any)?.vehicles && (originalForm as any).vehicles.length > 0
      ? (originalForm as any).vehicles
      : basicInformation?.vehicles || [];

  const firstVehicleName =
    vehiclesList.length > 0
      ? vehiclesList[0].manufacturer || vehiclesList[0].model || vehiclesList[0].carPlateNumber || 'Vehicle'
      : '';

  const vehicleExtraCount = vehiclesList.length > 1 ? vehiclesList.length - 1 : 0;

  const totalFavoriteCount =
    favoriteStationCount ||
    (originalForm as any)?.favouriteLocations?.length ||
    basicInformation?.favouriteLocations?.length ||
    0;

  const cardsList =
    (originalForm as any)?.cards && (originalForm as any).cards.length > 0
      ? (originalForm as any).cards
      : basicInformation?.cards || [];

  let paymentMethodDisplay = basicInformation?.hyperpayCustomerCard || '';
  if (!paymentMethodDisplay && cardsList.length > 0) {
    const card = cardsList[0];
    paymentMethodDisplay = `${card.brand || ''} ****${card.last4 || ''}`.trim() || card.gateway || 'Card';
  } else if (!paymentMethodDisplay && (originalForm as any)?.preferredPaymentMethod) {
    paymentMethodDisplay = (originalForm as any).preferredPaymentMethod;
  } else if (!paymentMethodDisplay) {
    paymentMethodDisplay = 'wallet';
  }

  const paymentExtraCount = cardsList.length > 1 ? cardsList.length - 1 : 0;

  const personalChargerCount =
    (originalForm as any)?.personalChargingPoints !== undefined
      ? (originalForm as any).personalChargingPoints.length
      : basicInformation?.personalChargingPoints?.length || 0;

  let rfidDisplay = '-';
  if ((originalForm as any)?.rfidCard?.length) {
    rfidDisplay = (originalForm as any).rfidCard
      .map((c: any) => c.rfidNo || c.cardNo || c)
      .filter(Boolean)
      .join(', ');
  } else if (basicInformation?.rfidCard?.length) {
    rfidDisplay = basicInformation.rfidCard
      .map((c) => c.rfidNo)
      .filter(Boolean)
      .join(', ');
  }

  const hasOriginalPhone = Boolean(originalForm?.phoneNumber?.trim());
  const hasOriginalEmail = Boolean(originalForm?.email?.trim());
  const isPhoneInvalid = hasOriginalPhone && !editForm?.phoneNumber?.trim();
  const isEmailInvalid = hasOriginalEmail && !editForm?.email?.trim();
  const isSaveDisabled = isPhoneInvalid || isEmailInvalid;

  return (
    <div className="ec-profile" dir={i18n.dir()}>
      {isModelOpen && (
        <SidePaneModel
          open={isModelOpen}
          title={modelData.title}
          content={modelData.content}
          closePanel={() => setIsModelOpen(false)}
          width={modelData.width}
        />
      )}
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />
      {/* =================== PROFILE + INFO GRID =================== */}
      <div className="ec-profile-info">
        <div className="card-header">
          <div className="card-header-item-image">
            {basicInformation?.user?.profileImage?.[0]?.signedUrl ? (
              <img
                src={basicInformation?.user?.profileImage?.[0]?.signedUrl}
                alt="profile"
                className="selected-image"
              />
            ) : (
              <img src={profile} alt="profile" />
            )}
            {consumerStatus && (
              <div className="consumer-status-container">
                <div className={`consumer-status ${consumerStatus}`}>
                  {consumerStatus === 'VIP' ? (
                    <p className="status-value">{consumerStatus.toUpperCase()}</p>
                  ) : (
                    <p className="status-value">
                      {consumerStatus.charAt(0).toUpperCase() + consumerStatus.slice(1)}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="info-group status">
              <div className={`value ${editForm?.isActive ? 'success' : 'error'}`}>
                <div className="color-circle" />
                <p>{editForm?.isActive ? t('active') : t('inactive')}</p>
              </div>
            </div>
          </div>
          <div className="card-header-item-title">
            <div className="card-header-details-grid">
              <div className="info-group">
                <p className="label">{t('name')}</p>
                <div className="value title-value">
                  {isEditMode ? (
                    <>
                      <div>
                        <CustomInput
                          type="text"
                          name="firstName"
                          value={editForm.firstName || ''}
                          onChangeValue={(name, value) => handleChange(name, String(value))}
                          placeholder={t('firstName')}
                          valueRequired={false}
                        />
                      </div>
                      <div className='last-name-field'>
                        <CustomInput
                          type="text"
                          name="lastName"
                          value={editForm.lastName || ''}
                          onChangeValue={(name, value) => handleChange(name, String(value))}
                          placeholder={t('lastName')}
                          valueRequired={false}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {editForm?.firstName} {editForm?.lastName}
                    </>
                  )}{' '}
                </div>
              </div>
              <div className="info-group">
                <p className="label">{t('phone')}</p>
                {isEditMode ? (
                  <div className="value">
                    <CustomPhoneInput
                      type="number"
                      name="phoneNumber"
                      placeholder={t('phoneNumber')}
                      value={editForm?.phoneNumber || ''}
                      value1={editForm?.countryCode}
                      onChangeValue={(name, value) => handleChange(name, String(value))}
                      valueRequired={hasOriginalPhone}
                      minLength={5}
                      maxLength={13}
                    />
                  </div>
                ) : (
                  <p className="value">{editForm?.phoneNumber || '-'}</p>
                )}{' '}
              </div>
              <div className="info-group">
                <p className="label">{t('consumerId')}</p>
                <div className="value">{consumerIdDisplay}</div>
              </div>
              <div className="info-group">
                <p className="label">{t('email')}</p>
                {isEditMode ? (
                  <div className="value">
                    <CustomInput 
                     type="email"
                     name="email"
                     value={editForm.email}
                     placeholder={t('enterEmail')}
                     onChangeValue={(name, value) => handleChange(name, String(value))}
                     valueRequired={hasOriginalEmail}
                    />
                  </div>
                ) : (
                  <p className="value">{editForm?.email || '-'}</p>
                )}{' '}
              </div>
            </div>
          </div>
          <div className="card-header-item-icon">
            {menuList.some((item) => item.allow) && (
              <DropDownMenu list={menuList} icon={<MoreIcon />} />
            )}
          </div>
        </div>

        <div className="ec-profile-details-grid">
          <div className="info-group">
            <p className="label">{t('rfidNo')}</p>
            <p className="value">{rfidDisplay}</p>
          </div>

          <div className="info-group">
            <p className="label">{t('personalCharger')}</p>
            <div
              className={`value hyper-link ${personalChargerCount ? '' : 'disabled'}`}
              role="presentation"
              onClick={personalChargerCount ? handlePersonalCharger : undefined}>
              {personalChargerCount ? (
                <p className="counts">{personalChargerCount}</p>
              ) : (
                <p className="value">-</p>
              )}
            </div>
          </div>

          <div className="info-group">
            <p className="label">{t('favoriteStations')}</p>
            <div
              className={`value hyper-link ${totalFavoriteCount ? '' : 'disabled'}`}
              role="presentation"
              onClick={totalFavoriteCount ? handleFavoriteStation : undefined}>
              {totalFavoriteCount ? (
                <p className="counts">{totalFavoriteCount}</p>
              ) : (
                <p className="value">-</p>
              )}
            </div>
          </div>

          <div className="info-group">
            <p className="label">{t('paymentMethod')}</p>
            <div className="ec-inline">
              <p
                className={`value hyper-link ${paymentMethodDisplay ? '' : 'disabled'}`}
                role="presentation"
                onClick={paymentMethodDisplay ? handleOpenPaymentMethod : undefined}>
                {paymentMethodDisplay || '-'}
              </p>
              {paymentExtraCount > 0 && (
                <p
                  className="value hyper-link"
                  role="presentation"
                  onClick={handleOpenPaymentMethod}>
                  <span className="counts">+{paymentExtraCount}</span>
                </p>
              )}
            </div>
          </div>

          <div className="info-group">
            <p className="label">{t('vehicle')}</p>
            <div className="ec-inline">
              <p
                className={`value hyper-link ${vehiclesList.length > 0 ? '' : 'disabled'}`}
                role="presentation"
                onClick={vehiclesList.length > 0 ? handleOpenVehicleDetails : undefined}>
                {firstVehicleName || '-'}
              </p>
              {vehicleExtraCount > 0 && (
                <p
                  className="value hyper-link"
                  role="presentation"
                  onClick={handleOpenVehicleDetails}>
                  <span className="counts">+{vehicleExtraCount}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bottom-actions">
          <MyButton
            label={t('activityLog')}
            buttonType="outline"
            onClickFunc={() => handleLogActivity()}
            svgIcon={<ActivityIcon />}
          />
          <MyButton
            label={t('resetPassword')}
            buttonType="submit"
            onClickFunc={() => resetPasswordFunction()}
            svgIcon={<ResetPasswordIcon />}
          />
        </div>

        {isEditMode && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <MyButton
              label={t('save')}
              buttonType="submit"
              loading={isSaveLoading}
              disableButton={isSaveDisabled || isSaveLoading}
              onClickFunc={() => {
                handleSave('edit');
              }}
            />
            <MyButton
              label={t('cancel')}
              buttonType="submit"
              onClickFunc={() => setIsEditMode(false)}
            />
          </div>
        )}
      </div>



      {/* =================== WALLET =================== */}
      {/* <div className="ec-profile-wallet">
        <div className="label">{t('walletBalance')}</div>
        <div className="value">{ToFixedValue(originalForm?.wallet?.balance) || 0} SAR</div>
      </div> */}
      <div className="ec-profile-wallet-container">
        <div className="ec-profile-wallet">
          <div className="wallet-details">
            <div className="text-wrapper">
              <div className="label">{t('walletBalance')}</div>
              <div className="value">{ToFixedValue(originalForm?.wallet?.balance) || 0} SAR</div>
            </div>
          </div>
          <div className="wallet-actions">
            <MyButton
              label={t('walletHistory')}
              buttonType="outline"
              onClickFunc={renderWalletHistory}
              svgIcon={<CardPosIcon />}
            />
            <MyButton
              label={t('addMoney')}
              buttonType="submit"
              onClickFunc={handleOpenModal}
              svgIcon={<CardPosIcon />}
            />
          </div>
        </div>
      </div>

      {/* =================== QUICK ACTIONS =================== */}
      {/* <div className="bottom-actions">
        <MyButton
          label={t('activityLog')}
          buttonType="outline"
          onClickFunc={() => handleLogActivity()}
          svgIcon={<ActivityIcon />}
        />
        <MyButton
          label={t('resetPassword')}
          buttonType="submit"
          onClickFunc={() => resetPasswordFunction()}
          svgIcon={<ResetPasswordIcon />}
        />
      </div> */}
    </div>
  );
}
